import { classMap } from 'lit-html/directives/class-map';
import { html, property } from 'lit-element';
import {
  Keys,
  KatLitElement,
  register,
  coerceToNumber,
  event,
  EventEmitter,
} from '../../shared/base';
import { ifNotNull } from '../../utils/directives';

import baseStyles from '../../shared/base/base.lit.scss';
import styles from './pagination.lit.scss';

// Max number of pages shown, otherwise it will ellipsify.
const MAX_PAGER_SEGMENTS = 7;
// Max distance current page is from end where ellipsis won't show
const MAX_DIFF_FROM_ENDPOINTS = 4;

/**
 * @component {kat-pagination} KatalPagination Pagination breaks large amounts of data into separate pages and allows the user to navigate between those pages.
 * @example Default {"total-items": "1000", "items-per-page": "10", "page":"4"}
 * @status Production
 * @theme flo
 */
@register('kat-pagination')
export class KatPagination extends KatLitElement {
  private __totalItems = 1;
  private __itemsPerPage = 10;
  private __page = 1;

  /** Deprecated - use `change` instead. Fired when the selected page changes via user interaction. */
  @event('page-changed')
  private _pagechanged: EventEmitter<{ page: number }>;

  /** Fires when the selected page changes via user interaction. */
  @event('change')
  private _change: EventEmitter<{ page: number }>;

  /**
   * The total number of items available for pagination.
   * @required
   */
  @property({ attribute: 'total-items' })
  get totalItems(): number {
    return this.__totalItems;
  }

  set totalItems(val: number) {
    const oldVal = this.totalItems;
    this.__totalItems = coerceToNumber(val);
    this.requestUpdate('totalItems', oldVal);
  }

  /**
   * The number of items that will be displayed on a single page.
   * Used to calculate total pages. Defaults to 10.
   */
  @property({ attribute: 'items-per-page' })
  get itemsPerPage(): number {
    return this.__itemsPerPage;
  }

  set itemsPerPage(val: number) {
    const oldVal = this.totalItems;
    this.__itemsPerPage = coerceToNumber(val);
    this.requestUpdate('itemsPerPage', oldVal);
  }

  /**
   * The currently selected page.
   * @required
   */
  @property()
  get page(): number {
    return this.__page;
  }

  set page(val: number) {
    const oldVal = this.totalItems;
    this.__page = coerceToNumber(val);
    this.requestUpdate('page', oldVal);
  }

  static get styles() {
    return [baseStyles, styles];
  }

  /**
   * The total number of pages, calculated from `totalItems` and `itemsPerPage`. Readonly.
   */
  get totalPages() {
    return Math.ceil(this.totalItems / (this.itemsPerPage || 1));
  }

  handleKeydown(e) {
    if (e.keyCode === Keys.Enter || e.keyCode === Keys.Space) {
      e.preventDefault();
      e.target.click();
      return;
    }

    const isLeft = e.keyCode === Keys.ArrowLeft;
    const isRight = e.keyCode === Keys.ArrowRight;

    if (isLeft || isRight) {
      e.preventDefault();

      let n = e.target;

      const next = () => {
        if (isLeft) {
          return (n = n.previousElementSibling);
        }
        return (n = n.nextElementSibling);
      };

      while (next() && n.classList.contains('ellipsis')) {
        // n will be next non-ellipsis or null
      }

      if (n) {
        n.focus();
      }
    }
  }

  handleNavClick(e) {
    if (!e.currentTarget.previousElementSibling) {
      if (this.page > 1) {
        this.page--;
        this._pageChanged();
      }
    } else {
      if (this.page < this.totalPages) {
        this.page++;
        this._pageChanged();
      }
    }
  }

  handlePageClick(e) {
    const pageClicked = parseInt(e.currentTarget.dataset.page);
    if (pageClicked !== this.page) {
      this.page = pageClicked;
      this._pageChanged();
    }
  }

  _pageChanged() {
    // @deprecated
    this._pagechanged.emit({ page: this.page });
    this._change.emit({ page: this.page });
  }

  _generatePages() {
    const currentPage = this.page;
    const pages = [];

    const generatePages = (startPageInclusive, endPageExclusive) => {
      for (let i = startPageInclusive; i <= endPageExclusive; ++i) {
        const selected = i === currentPage;
        pages.push(html`
          <li
            part=${`pagination-page-${i}`}
            tabindex="0"
            data-page=${i}
            aria-current=${ifNotNull(selected ? 'true' : null)}
            class="item page"
            @click=${this.handlePageClick}
          >
            <span class="page__inner">${i}</span>
          </li>
        `);
      }
    };

    const generateEllipsis = () => {
      pages.push(html`
        <li class="item ellipsis"><span class="page__inner">...</span></li>
      `);
    };

    let leftEllipsis = false;
    let rightEllipsis = false;

    if (this.totalPages > MAX_PAGER_SEGMENTS) {
      // only show left ellipsis when there are more than `MAX_PAGER_SEGMENTS`
      // total pages and the user has clicked on the 5th page.
      leftEllipsis = currentPage > MAX_DIFF_FROM_ENDPOINTS;
      // only show right ellipsis when there are more than `MAX_PAGER_SEGMENTS`
      // pages and the user is within 4 pages of the total number of pages
      rightEllipsis = this.totalPages - currentPage >= MAX_DIFF_FROM_ENDPOINTS;
    }

    if (!leftEllipsis && !rightEllipsis) {
      // neither ellipsis is set, write all the numbers out
      generatePages(1, this.totalPages);
    } else if (leftEllipsis && !rightEllipsis) {
      // only the left ellipsis is being set, the first number will always exist
      generatePages(1, 1);
      generateEllipsis();

      // next we need to write out up to the last `MAX_DIFF_FROM_ENDPOINTS` pages
      generatePages(this.totalPages - MAX_DIFF_FROM_ENDPOINTS, this.totalPages);
    } else if (!leftEllipsis && rightEllipsis) {
      // we need to write out the first `MAX_DIFF_FROM_ENDPOINTS` numbers, an ellipsis, and the max number
      generatePages(1, MAX_DIFF_FROM_ENDPOINTS + 1);
      generateEllipsis();
      generatePages(this.totalPages, this.totalPages);
    } else {
      // both ellipsis are being written - we need the first number, ellipsis,
      // currentpage - 1, currentpage, currentpage + 1, ellipsis, lastnumber
      generatePages(1, 1);
      generateEllipsis();
      generatePages(currentPage - 1, currentPage + 1);
      generateEllipsis();
      generatePages(this.totalPages, this.totalPages);
    }

    return pages;
  }

  render() {
    const currentPage = this.page;

    const firstPage = currentPage === 1;
    const lastPage = currentPage === this.totalPages;

    const pages = this._generatePages();

    return html`
      <nav @keydown=${this.handleKeydown}>
        <span
          tabindex="0"
          class=${classMap({ nav: true, item: true, end: firstPage })}
          part="pagination-nav-left"
          @click=${this.handleNavClick}
        >
          <kat-icon class="nav__icon" name="chevron-left"></kat-icon>
        </span>
        <ul class="pages">
          ${pages}
        </ul>
        <span
          tabindex="0"
          part="pagination-nav-right"
          class=${classMap({ nav: true, item: true, end: lastPage })}
          @click=${this.handleNavClick}
        >
          <kat-icon class="nav__icon" name="chevron-right"></kat-icon>
        </span>
      </nav>
    `;
  }
}
